import React from "react";
import { Column, Heading, Paragraph, Row } from "@hex-insights/core";
import { EditMode, Form, SubmitButton } from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import { ButtonLink, UserDetailQuery, UserField, UserForm, useUserDetailQuery } from "@s-i-s-i/see-learning.shared";
import { AuthenticationContext } from "../../../Contexts";
import { OnboardingLayout } from "../Components";
import { onboardingDisplayNamePageInfo } from "../DisplayNamePage/pageinfo";
import { onboardingSuccessPageInfo } from "../SuccessPage/pageinfo";
import { onboardingNotificationsPageInfo } from "./pageinfo";

export function OnboardingNotificationsPage() {
	useActivePageRegistration(onboardingNotificationsPageInfo);

	const { user: authUser } = React.useContext(AuthenticationContext);
	const { data } = useUserDetailQuery({ variables: { id: authUser?.id ?? "0" }, skip: !authUser });
	const user = data?.user;

	if (!user) {
		return <OnboardingLayout page="notifications">Loading...</OnboardingLayout>;
	}

	return <LoadedContent user={user} />;
}

const userProfileFields = {
	externalNotificationFrequency: true,
} as const;

type LoadedContentProps = {
	user: UserDetailQuery["user"];
};

function LoadedContent({ user }: LoadedContentProps) {
	const history = useHistory();
	const onSuccess = React.useCallback(() => {
		history.push(onboardingSuccessPageInfo.to);
	}, [history]);
	const { formState, onSubmit } = UserForm.useDetailForm({
		user,
		onSuccess,
		fields: userProfileFields,
		editMode: EditMode.WriteOnly,
	});

	return (
		<OnboardingLayout
			page="notifications"
			footerContent={
				<Row justify="space-between">
					<ButtonLink variant="tertiary" to={onboardingDisplayNamePageInfo.to}>
						Back
					</ButtonLink>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Save &amp; Continue
					</SubmitButton>
				</Row>
			}
		>
			<Form
				formState={formState}
				name={`onboarding.displayName.${user.id}`}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				noNotifications
				noNavigationPrompt
				noChangesDialog
				mergeUnsavedChangesAutomatically
			>
				<Form.Column verticalSpacing="1.5rem" style={{ margin: "0 auto" }}>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Heading.H2 noMargin>
							How often would you like to receive email notifications from the SEE Learning Portal?
						</Heading.H2>

						<Paragraph>We'll only email you if there's something important to share.</Paragraph>
					</Column>

					<UserField.ExternalNotificationFrequency formState={formState} noHeader />

					<Paragraph>
						Based on your choice here we will aggregate the messages so that we avoid sending you too many
						notifications. Please note that urgent messages will be sent separately.
					</Paragraph>
				</Form.Column>
			</Form>
		</OnboardingLayout>
	);
}
